import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Inject,
	Input, OnDestroy, OnInit,
	Output
} from '@angular/core';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { BehaviorSubject, map, Observable, combineLatest, filter } from 'rxjs';
import { AppDataService, appDataServiceToken } from 'dashboard/services/app-data.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { groupToken } from 'go-modules/models/group-dep/group.factory';
import { GroupType } from '@go-modules/enums/group-type';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { FeatureFlagDialogComponent } from '@go-modules/components/dialogs/feature-flag-dialog/feature-flag-dialog.component';
import { ENVIRONMENT_CONSTANTS } from 'go-modules/constants/environment-constants.constant';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { ENVIRONMENTS } from '@go-modules/services/environment-vars/environments';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { CustomParamsDialogComponent, CustomParamsDialogData } from '../../dialogs/custom-params-dialog/custom-params-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { HeyGenPromptEditorComponent } from '../../hey-gen-prompt-editor/hey-gen-prompt-editor.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomerSurveySnackBarComponent, SurveyMode } from '@go-modules/components/snack-bars/customer-survey/customer-survey-snack-bar.component';
import { InviteUsersDialogComponent } from '@go-modules/components/dialogs/invite-users-dialog/invite-users-dialog.component';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import {
	LibraryDialogComponent,
	type LibraryDialogContext
} from '@go-modules/components/dialogs/library-dialog/library-dialog.component';

const TOOLTIP_DEFAULTS: MatTooltipDefaultOptions = {
	position: 'right',
	showDelay: 0,
	hideDelay: 0,
	touchendHideDelay: 0
};

@Component({
	selector: 'dashboard-navigation',
	templateUrl: './dashboard-navigation.component.html',
	styleUrls: ['./dashboard-navigation.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
	host: {
		'role': 'navigation',
		'aria-label': 'Dashboard navigation'
	},
	providers: [
		{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: TOOLTIP_DEFAULTS }
	]
})
export class DashboardNavigationComponent implements OnInit, OnDestroy {
	@Input() public collapsed$: BehaviorSubject<boolean>;
	@Input() public showMessage$: BehaviorSubject<boolean>;
	@Input() public isMobile$: BehaviorSubject<boolean>;
	@Output() public toggleSideNav = new EventEmitter<void>();
	public transitionState$ = new BehaviorSubject<ROUTE_NAMES>(null);
	public states = ROUTE_NAMES;
	public unsubscribe;
	public nonFolderStates = [
		ROUTE_NAMES.LICENSE_MANAGEMENT,
		ROUTE_NAMES.DASHBOARD_LICENSE_MANAGEMENT_ORG,
		ROUTE_NAMES.LICENSE_SEATS_MANAGEMENT,
		ROUTE_NAMES.DASHBOARD_ARCHIVE,
		ROUTE_NAMES.DASHBOARD_SETTINGS
	];
	public licenseStates = [
		ROUTE_NAMES.LICENSE_MANAGEMENT,
		ROUTE_NAMES.DASHBOARD_LICENSE_MANAGEMENT_ORG,
		ROUTE_NAMES.LICENSE_SEATS_MANAGEMENT
	];
	private readonly groupContextStates = [
		ROUTE_NAMES.DASHBOARD_FOLDER_VIEW,
		ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW,
		ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK,
		ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK_REPORT,
		ROUTE_NAMES.DASHBOARD_ACTIVITY_REPORT,
		ROUTE_NAMES.DASHBOARD_SESSION_VIEW
	];
	public environmentVarsService: EnvironmentVarsService;
	public shouldShowFeaturedMessage$: Observable<boolean>;
	private readonly PARTNER_GOREACT = 'GO_REACT';

	constructor (
		@Inject(selectedServiceToken) public selectedService: SelectedService,
		@Inject(appDataServiceToken) public AppData: AppDataService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(groupToken) private group,
		private modal: GoModalService,
		private authService: NgxAuthService,
		private dialog: MatDialog,
		private featureFlag: NgxFeatureFlagService,
		private snackbar: MatSnackBar,
		private routerService: RouterService,
		private router: Router
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
	}

	public get whiteLabel (): boolean {
		return this.environmentVarsService.get(EnvironmentVarsService.VAR.WHITELABEL) as boolean;
	}

	public get isLTI (): boolean {
		return this.environmentVarsService.environmentIs(ENVIRONMENTS.LTI);
	}

	public get orgs () {
		if (this.isLTI) {
			return this.selectedService.getOrg() ? [this.selectedService.getOrg()] : [];
		}

		return this.selectedService.getMyOrgs();
	}

	public get customParams () {
		return this.environmentVarsService.get(EnvironmentVarsService.VAR.CUSTOM_PARAMS);
	}

	public get isPresenter (): boolean {
		return this.userService.currentUser.isPresenter;
	}

	public ngOnInit (): void {
		this.transitionState$.next(this.routerService.getCurrentRouteName());

		this.router.events.pipe(
			filter((event) => event instanceof NavigationEnd)
		).subscribe(() => {
			this.transitionState$.next(this.routerService.getCurrentRouteName());
		});

		if (this.userService.currentUser.isInstructor && this.featureFlag.isAvailable('AI_TRIAL')) {
			this.checkFeaturedMessageVisibility();
		}
	}

	public ngOnDestroy () {
		if(this.unsubscribe) this.unsubscribe();
	}

	public checkFeaturedMessageVisibility () {
		this.shouldShowFeaturedMessage$ = combineLatest([
			this.showMessage$,
			this.collapsed$
		])
			.pipe(
				map(([showMessage, isCollapsed]) => showMessage && !isCollapsed)
			);
	}

	public goToOrg (org) {
		this.selectedService.setOrg(this.group.model(org));

		if (!this.isLTI) {
			this.selectedService.setGroup(null);
		}

		this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_FOLDERS, {id: org.group_id});
	}

	public onOrgLinkClick (event: MouseEvent, org): void {
		event.preventDefault();
		this.goToOrg(org);
	}

	public isOrgSelected (org, currentRoute: ROUTE_NAMES | null): boolean {
		return org.group_id === this.selectedService.getOrg()?.group_id &&
			!this.nonFolderStates.includes(currentRoute);
	}

	public goToArchive () {
		this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ARCHIVE);
	}

	public onArchiveLinkClick (event: MouseEvent): void {
		event.preventDefault();
		this.goToArchive();
	}

	public goToLicense () {
		this.routerService.navigateTo(ROUTE_NAMES.LICENSE_MANAGEMENT);
	}

	public onLicenseLinkClick (event: MouseEvent): void {
		event.preventDefault();
		this.goToLicense();
	}

	public openFeatureFlags () {
		this.modal.open(FeatureFlagDialogComponent);
	}

	public shouldShowFeatureFlags () {
		return this.userService.currentUser.is_root_user &&
			ENVIRONMENT_CONSTANTS.APP_ENVIRONMENT !== ENVIRONMENT_CONSTANTS.PROD;
	}

	public shouldShowHeyGen () {
		return this.featureFlag.isAvailable('HEYGEN');
	}

	public openHeyGen () {
		this.modal.open(HeyGenPromptEditorComponent);
	}

	public openHelp () {
		window.open('https://support.echo360.com/hc/en-us/categories/37644637471373-GoReact', '_blank');
	}

	public openLibrary () {
		this.modal.open(LibraryDialogComponent, false, {
			data: {
				context: this.getLibraryDialogContext()
			},
			focusReturnSelector: '[data-cy="library-button"]'
		});
	}

	private getLibraryDialogContext (): LibraryDialogContext {
		const currentRouteName = this.routerService.getCurrentRouteName();
		const hasGroupContext = currentRouteName != null && this.groupContextStates.includes(currentRouteName);

		return {
			group: hasGroupContext ? this.selectedService.getGroup() : null,
			account: this.selectedService.getAccount(),
			org: this.selectedService.getOrg(),
			license: hasGroupContext ? this.selectedService.getLicense() : null
		};
	}

	public shouldShowLibraryButton () {
		// DASHBOARD_ROOT redirects quickly, but cant show library button
		// while we wait or else there is no selected org and e2es wont work
		if (this.routerService.getCurrentRouteName() === ROUTE_NAMES.DASHBOARD_ROOT) {
			return false;
		}
		if (this.hasNoSelectedGroupAndAccount()) {
			return false;
		}
		return this.userService.currentUser.isInstructor || !!this.AppData.my_account;
	}

	public shouldShowLicenseManagement () {
		const partners = this.isLTI ? clientSettings.partners : this.AppData.partners;
		const isGoReactPartner = partners.every((value) => value.guid === this.PARTNER_GOREACT);

		return (this.userService.currentUser.isInstructor || this.userService.currentUser.is_root_user) &&
			isGoReactPartner;
	}

	public openFeedbackSnackbar () {
		CustomerSurveySnackBarComponent.open(this.snackbar, SurveyMode.FEEDBACK);
	}

	public canInvite (_org): boolean {
		if(this.isLTI) return;

		const org = this.group.model(_org);
		const groups = this.selectedService.getGroups();
		const hasInstructorRole = groups.some((gr) => gr.org_id === org.group_id && gr.hasInstructorRole(true));
		return !!org && !org.placeholder &&
			(org.hasAdminRole(true) || hasInstructorRole || this.userService.currentUser.is_root_user);
	}

	public openInviteModal (event, _org): void {
		event.preventDefault();
		event.stopPropagation();
		const org = this.group.model(_org);
		if (!org.role) {
			org.role = this.group.role.INSTRUCTOR;
		}

		InviteUsersDialogComponent.open(this.dialog, {
			data: {
				group: org
			}
		});
	}

	public shouldShowCustomParams (): boolean {
		return this.isLTI && this.authService.isAdmin() && this.customParams;
	}

	public openCustomParamsModal (): void {
		CustomParamsDialogComponent.open(this.dialog, {
			data: {
				resource_link_id: this.customParams.resource_link_id,
				context_id: this.customParams.context_id,
				user_id: this.customParams.user_id
			} as CustomParamsDialogData
		});
	}

	private hasNoSelectedGroupAndAccount () {
		return this.selectedService.getGroup() === null &&
			(!this.selectedService.getAccount() || this.selectedService.getAccount().type !== GroupType.ACCOUNT);
	}

	private buildHref (routeName: ROUTE_NAMES, params?: Record<string, any>): string {
		let commands = this.routerService.buildUrl(routeName, params);
		if (this.isLTI && commands[0] === 'dashboard') {
			commands = commands.slice(1);
		}

		return `/${commands.join('/')}`;
	}

	public getOrgHref (org): string {
		return this.buildHref(ROUTE_NAMES.DASHBOARD_FOLDERS, { id: org.group_id });
	}

	public getArchiveHref (): string {
		return this.buildHref(ROUTE_NAMES.DASHBOARD_ARCHIVE);
	}

	public getLicenseHref (): string {
		return this.buildHref(ROUTE_NAMES.LICENSE_MANAGEMENT);
	}
}
